<script lang="ts">
  import { isAvailable, type MenuData, type MenuItem } from "../../lib/helpers/steps/menuItems";
  import ItemSelector from "./ItemSelector.svelte";
  import Button from "../Button.svelte";
  import Carousel from "../Carousel.svelte";
  import VerticalMenuItemCard from "./VerticalMenuItemCard.svelte";
  import HorizontalMenuItemCard from "./HorizontalMenuItemCard.svelte";
  import type { EmblaCarouselType } from "embla-carousel";
  import { getCopy } from "../../lib/stores/copy";
  import { saveMultipleSelectionChoices, clearUnavailableMultipleSelections } from "../../lib/helpers/steps/menu";
  import {
    sessionStore,
    NavigationType,
    upsertMultipleSelectionItem,
    removeMultipleSelectionItem
  } from "../../lib/stores/session";
  import { navigateTo } from "../../lib/routing";
  import LoadingScreen from "../LoadingScreen.svelte";
  import { onMount } from "svelte";

  export let menuData: MenuData;
  export let isSaving = false;
  export let requiredSelectionCount: number;

  let isSelectorOpen = false;
  let emblaApi: EmblaCarouselType;
  let continueButton: { scrollIntoView: () => void };

  onMount(() => clearUnavailableMultipleSelections(menuData.menuItems));

  let selectedIndex: number;

  function closeSelector() {
    isSelectorOpen = false;
  }

  const mainButtonClick = async () => {
    try {
      isSaving = true;
      await saveMultipleSelectionChoices();
      navigateTo(NavigationType.Next);
    } catch (error) {
      console.error("Error saving multiple selection choices", error);
      // FIXME: Bugsnag? We should also show an error to the user
      isSaving = false;
    }
  };

  const handleVariationConfirmed = (index: number, event: CustomEvent<string>) => {
    const item = menuData.menuItems[index];

    upsertMultipleSelectionItem({
      item_key: item.itemKey!,
      variation_key: event.detail,
      api: item.variations.find((variation) => variation.name === event.detail)?.api
    });
    isSelectorOpen = false;
  };

  const handleMenuItemClick = (index: number, item: MenuItem) => {
    const foundSelectedItem = $sessionStore.multipleSelectionSelectedItems?.find(
      (selectedItem) => selectedItem.item_key === item.itemKey
    );

    if (foundSelectedItem) {
      removeMultipleSelectionItem(item.itemKey!);
    } else if (item.variations?.length > 1) {
      selectedIndex = index;
      isSelectorOpen = true;
    } else {
      upsertMultipleSelectionItem({
        item_key: item.itemKey!,
        variation_key: item.variations[0].name,
        api: item.variations[0].api
      });
    }
  };

  const getSelectedVariation = (index: number, selectedItems: { item_key: string; variation_key: string }[]) => {
    const item = menuData.menuItems[index];
    const foundSelectedItem = selectedItems.find((selectedItem) => selectedItem.item_key === item.itemKey);

    return foundSelectedItem?.variation_key;
  };

  $: isComplete = $sessionStore.multipleSelectionSelectedItems?.length === requiredSelectionCount;
  $: {
    if (isComplete) {
      // The button might not exist when this code first runs
      continueButton?.scrollIntoView();
    }
  }
  $: copy = $getCopy("chooseItem") as Record<string, string>;

  $: selectedVariations = menuData.menuItems.map((_, index) =>
    getSelectedVariation(index, $sessionStore.multipleSelectionSelectedItems ?? [])
  );
</script>

{#if isSelectorOpen}
  <ItemSelector
    on:close={closeSelector}
    item={menuData.menuItems[selectedIndex]}
    on:confirm={(event) => handleVariationConfirmed(selectedIndex, event)} />
{/if}

{#if isSaving}
  <LoadingScreen />
{:else}
  <div class="w-full mx-auto text-center lg:text-left">
    {#if menuData.menuItems}
      <div class="flex lg:hidden pt-4 content">
        <Carousel bind:emblaApi>
          {#each menuData.menuItems as item, index}
            <div class="embla__slide">
              <div class="flex justify-center h-full">
                <VerticalMenuItemCard
                  {item}
                  on:click={() => handleMenuItemClick(index, item)}
                  active={Boolean(selectedVariations[index])}
                  unavailable={(isComplete && !selectedVariations[index]) || !isAvailable(item)}
                  useThickActiveBorder={true}
                  selectedVariation={selectedVariations[index]} />
              </div>
            </div>
          {/each}
        </Carousel>
      </div>

      <div class="flex-col mt-8 gap-6 hidden lg:flex items-center">
        {#each menuData.menuItems as item, index}
          <HorizontalMenuItemCard
            {item}
            on:click={() => handleMenuItemClick(index, item)}
            active={Boolean(selectedVariations[index])}
            unavailable={(isComplete && !selectedVariations[index]) || !isAvailable(item)}
            useThickActiveBorder={true}
            selectedVariation={selectedVariations[index]} />
        {/each}
      </div>
    {/if}
    <div class="flex w-full justify-center lg:justify-start mt-6 order-button lg:mt-12">
      <Button
        bind:this={continueButton}
        class="w-full lg:w-auto"
        primary
        on:click={mainButtonClick}
        disabled={!isComplete}>
        {copy.orderButtonCopy}
      </Button>
    </div>
  </div>
{/if}

<style>
  .embla__slide {
    flex: 0 0 var(--slide-size);
    min-width: 0;
    padding-left: var(--slide-spacing);
  }
</style>
